import {
  FormAssignmentConfigDto,
  UpdateFormAssignmentQuestionCodesDto,
} from "@green0meter/api-client";

import { ParentSelection } from "@components/common/Table";

/*
 * Filters out fully selected sections and their questions from the selection.
 * Returns only question IDs that are selected individually or belong to indeterminate sections.
 */
export const getRelevantSectionsAndQuestions = (
  selectedSections: ParentSelection[],
  selectedQuestionCodes: UpdateFormAssignmentQuestionCodesDto[],
  formAssignmentConfig?: FormAssignmentConfigDto,
): {
  sectionCodes: string[];
  questionCodes: UpdateFormAssignmentQuestionCodesDto[];
} => {
  if (!formAssignmentConfig) {
    return {
      sectionCodes: [],
      questionCodes: [],
    };
  }

  const indeterminateSectionCodes = selectedSections
    .filter((s) => s.indeterminate)
    .map((s) => s.id);

  const questionCodesInIndeterminateSections: UpdateFormAssignmentQuestionCodesDto[] =
    [];

  indeterminateSectionCodes.forEach((sectionCode) => {
    const sectionConfig = formAssignmentConfig?.formSections.find(
      (s) => s.code === sectionCode,
    );

    if (sectionConfig) {
      const allQuestionCodes = sectionConfig.formQuestions.map(
        (q) => q.questionCode,
      );
      const selectedQuestionCodesInSection = selectedQuestionCodes.filter((qId) =>
        allQuestionCodes.find(
          (code) => code === qId.questionCode && sectionCode === qId.sectionCode,
        ),
      );

      questionCodesInIndeterminateSections.push(...selectedQuestionCodesInSection);
    }
  });

  return {
    sectionCodes: selectedSections
      .filter((s) => !indeterminateSectionCodes.includes(s.id))
      .map((s) => s.id),
    questionCodes: questionCodesInIndeterminateSections,
  };
};

/*
 * Handles the selection change of sections. Updates selected sections and question IDs accordingly.
 * If a section is selected, all its questions are selected unless it was previously indeterminate.
 * In that case, only the previously selected question IDs within that section are retained.
 */
export const getSectionSelectionChange = (
  selectedCodes: string[],
  formAssignmentConfig: FormAssignmentConfigDto,
  selectedSections: ParentSelection[],
  selectedQuestionCodes: UpdateFormAssignmentQuestionCodesDto[],
): {
  checkedQuestionCodes: UpdateFormAssignmentQuestionCodesDto[];
  newSelectedSections: ParentSelection[];
} => {
  const newSelectedSections: ParentSelection[] = [];
  const checkedQuestionCodes: UpdateFormAssignmentQuestionCodesDto[] = [];

  selectedCodes.forEach((code) => {
    let isIndeterminate = false;
    const section = formAssignmentConfig?.formSections.find((s) => s.code === code);

    if (section) {
      const selectedSectionsEntry = selectedSections.find((s) => s.id === code);

      // If section is indeterminate, we need to keep the previously selected question IDs
      if (selectedSectionsEntry?.indeterminate) {
        isIndeterminate = true;

        section.formQuestions.forEach((q) => {
          if (
            selectedQuestionCodes.find(
              (qId) =>
                qId.questionCode === q.questionCode && qId.sectionCode === code,
            )
          ) {
            checkedQuestionCodes.push({
              questionCode: q.questionCode,
              sectionCode: code,
            });
          }
        });
      } else {
        const allQuestionCodes: UpdateFormAssignmentQuestionCodesDto[] =
          section.formQuestions.map((q) => ({
            questionCode: q.questionCode,
            sectionCode: code,
          }));

        checkedQuestionCodes.push(...allQuestionCodes);
      }

      // Set selected sections with indeterminate state
      newSelectedSections.push({ id: code, indeterminate: isIndeterminate });
    }
  });

  return {
    checkedQuestionCodes,
    newSelectedSections,
  };
};
